import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../services/AuthContext";
import { ButtonPrimary, Badge, Icon } from "./ComponentLibrary";
import { Menu, X, LogOut, User as UserIcon } from "lucide-react";
import NotificationBell from "./NotificationBell";
import "./HeaderNew.css";
import logoImg from "../img/Untitled design (19).png";

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  // Close menu when route changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  // Track scroll for sticky effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent background scroll when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [menuOpen]);

  const toggleMenu = () => setMenuOpen(!menuOpen);

  return (
    <header className={`main-header ${scrolled ? 'scrolled' : ''}`}>
      <div className="header-container">
        {/* 1. Logo Section */}
        <div className="brand">
          <NavLink to="/" className="logo-link">
            <div className="logo-icon-wrapper">
              <img src={logoImg} alt="Code Wave Logo" className="logo-img" width="42" height="42" />
            </div>
            <div className="brand-text">
              <span className="code-text">Code</span>
              <span className="wave-text">Wave</span>
            </div>
          </NavLink>
        </div>

        {/* 2. Desktop Navigation */}
        <nav className="desktop-nav">
          <NavLink to="/" end className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>الرئيسية</NavLink>

          {user && user.role === 'student' && (
            <>
              <NavLink to="/student-portal" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>لوحتي</NavLink>
              <NavLink to="/lessons" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>الدروس</NavLink>
              <NavLink to="/ai-tutor" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>المساعد الذكي</NavLink>
            </>
          )}

          {user && user.role === 'admin' && (
            <NavLink to="/admin" className="nav-link admin-link">الإدارة <Icon name="parent" size={16} /></NavLink>
          )}

          <NavLink to="/dashboard" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>الأسعار</NavLink>
          <NavLink to="/about" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>عن المنصة</NavLink>
        </nav>

        {/* 3. Global Actions */}
        <div className="header-actions">
          {user ? (
            <div className="user-profile-nav">
              <NotificationBell />
              <div className="user-avatar-menu">
                <div className="avatar">
                  {user.fullName ? user.fullName[0] : 'U'}
                </div>
                <div className="user-dropdown-info">
                  <span className="user-name-label">{user.fullName || user.name}</span>
                  <button className="logout-icon-btn" onClick={logout} title="تسجيل الخروج">
                    <LogOut size={18} />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="auth-buttons">
              <NavLink to="/login" className="login-link">دخول</NavLink>
              <ButtonPrimary size="sm" onClick={() => (window.location.href = '/register')}>
                انضم إلينا <Icon name="rocket" size={16} />
              </ButtonPrimary>
            </div>
          )}

          {/* Mobile Toggle */}
          <button className="mobile-toggle" onClick={toggleMenu}>
            {menuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* 4. Mobile Navigation Overlay */}
      <div className={`mobile-nav-overlay ${menuOpen ? 'open' : ''}`} onClick={() => setMenuOpen(false)}>
        <nav className="mobile-nav-content" onClick={e => e.stopPropagation()}>
          <div className="mobile-nav-header">
            <div className="logo-text">CodeWave</div>
            <button onClick={toggleMenu}><X size={24} /></button>
          </div>

          <div className="mobile-links">
            <NavLink to="/" end>الرئيسية</NavLink>
            {user && user.role === 'student' && (
              <>
                <NavLink to="/student-portal">لوحة التحكم</NavLink>
                <NavLink to="/lessons">الدروس</NavLink>
                <NavLink to="/ai-tutor">المساعد الذكي</NavLink>
              </>
            )}
            <NavLink to="/dashboard">الأسعار والخطط</NavLink>
            <NavLink to="/about">عن المنصة</NavLink>
          </div>

          <div className="mobile-auth">
            {user ? (
              <div className="mobile-user-actions">
                <div className="mobile-user-info">
                  <div className="avatar-large">{user.fullName ? user.fullName[0] : 'U'}</div>
                  <span>{user.fullName || user.name}</span>
                </div>
                <button className="mobile-logout-btn" onClick={logout}>تسجيل الخروج</button>
              </div>
            ) : (
              <div className="mobile-auth-btns">
                <NavLink to="/login" className="mobile-login-btn">تسجيل الدخول</NavLink>
                <NavLink to="/register" className="mobile-register-btn">انضم إلينا</NavLink>
              </div>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
}
